import _ from 'lodash';
import { CardType } from '@scale/genai-compass-shared/cardTypes';
import type { SnowflakeSqlContext } from '@scale/genai-compass-shared/cards/snowflake_sql';
import { RunType, PassAtKRunStatus, EvaluationType } from '@scale/shared/types/passAtK';
import { Badge } from '@scale/scaleui-radix';
import { ChatApiEndpointWithId, ProjectChatApiEndpointWithId } from '@scale/shared/types/chat';

export type ModelConfig = (ChatApiEndpointWithId | ProjectChatApiEndpointWithId) & {
  modelEndpointId?: string;
};

export const DEFAULT_GTFA_PROMPT = `You are a {{topic}} expert. Your response should be in the following format:
explanation: your explanation for your final answer
final_answer: your succinct, final answer
Problem Statement:
{{prompt}}`;

// TODO(linhhuy): Keeping for future reference
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const OLD_DEFAULT_HLE_GRADING_PROMPT = `You are a {{topic}} expert. Judge whether the following [response] to [question] is correct or not based on the precise and unambiguous [correct_answer] below.

[question]: {{prompt}}

[response]: {{model_answer}}

[correct_answer]: {{gtfa}}

Please provide your evaluation in the following JSON format:
{
  "extracted_final_answer": <The final exact answer extracted from the [response]. Put the extracted answer as 'None' if there is no exact, final answer to extract from the response.>,
  "correct": "TRUE" if extracted_final_answer matches the [correct_answer] given above, or is within a small margin of error for numerical problems. "FALSE" otherwise, i.e. if there if there is any inconsistency, ambiguity, non-equivalency, or if the extracted answer is incorrect.
  "justification": <Explanation for why the extracted_final_answer is correct or incorrect based on [correct_answer], focusing only on if there are meaningful differences between [correct_answer] and the extracted_final_answer. Do not comment on any background to the problem, do not attempt to solve the problem, do not argue for any answer different than [correct_answer], focus only on whether the answers match.>,
  "confidence": <The extracted confidence score between 0|%| and 100|%| from [response]. Put 100 if there is no confidence score available.>
}
Do not include any other text before or after the JSON object.`;

const DEFAULT_HLE_GRADING_PROMPT = `You are a strict evaluator and an expert in {{topic}}. Determine whether the [response] is equivalent to the [correct_answer] which is the GROUND TRUTH.
Guidelines:
- Judge semantic equivalence, not formatting.
- Treat LaTeX/math as equivalent if it represents the same expression (order/commutativity OK).
- For polynomials: accept re-ordered terms (e.g., 1 + X + X^3 + X^5 == X^5 + X^3 + X + 1).
- For complexity: accept standard equivalents only when they truly match (Theta vs O matters).
- If the response has multiple conflicting answers, mark incorrect unless one is clearly the intended final answer.
- If you cannot confidently decide, return "unknown".

[question]: {{prompt}}
[response]: {{model_answer}}
[correct_answer]: {{gtfa}}

Please provide your evaluation in the following JSON format:
{
  "extracted_final_answer": <The final exact answer extracted from the [response]. Put the extracted answer as 'None' if there is no exact, final answer to extract from the response.>,
  "correct": "TRUE" if extracted_final_answer matches the [correct_answer] given above guidelines only. "FALSE" otherwise.
  "justification": <Explain your decision in one sentence only>,
  "confidence": <The extracted confidence score between 0|%| and 100|%| from [response].>
}
Do not include any other text before or after the JSON object.`;

const DEFAULT_RUBRICS_GRADING_PROMPT = `You are an expert evaluator. Given a user prompt, a generated response, and a list of quality rubrics, please evaluate the response against EACH rubric.

For each rubric, mark "PASS" if the criterion is satisfied, or "FAIL" if it is not.

<prompt>
{{prompt}}
</prompt>

<response>
{{model_answer}}
</response>

<rubrics>
{{rubrics}}
</rubrics>

Please provide your evaluation in the following JSON format:
{
  "rubric_id_1": "PASS" or "FAIL",
  "rubric_id_2": "PASS" or "FAIL",
  ...
}

The keys must be the rubric IDs provided and the values must be either "PASS" or "FAIL" based on your evaluation.
Do not include any other text before or after the JSON object.`;

export const DEFAULT_GRADING_PROMPTS: Record<EvaluationType, string> = {
  [EvaluationType.HLE]: DEFAULT_HLE_GRADING_PROMPT,
  [EvaluationType.Rubrics]: DEFAULT_RUBRICS_GRADING_PROMPT,
};

export const AUTOMATICALLY_INCLUDED_COLUMNS = ['model_answer'];
export const ALWAYS_REQUIRED_COLUMNS = ['task_id'];
export const REQUIRED_GTFA_PROMPT_VARIABLES = ['prompt'];
const REQUIRED_HLE_GRADING_PROMPT_VARIABLES = ['prompt', 'gtfa', ...AUTOMATICALLY_INCLUDED_COLUMNS];
const REQUIRED_RUBRICS_GRADING_PROMPT_VARIABLES = [
  'prompt',
  'rubrics',
  ...AUTOMATICALLY_INCLUDED_COLUMNS,
];
export const REQUIRED_GRADING_PROMPT_VARIABLES: Record<EvaluationType, string[]> = {
  [EvaluationType.HLE]: REQUIRED_HLE_GRADING_PROMPT_VARIABLES,
  [EvaluationType.Rubrics]: REQUIRED_RUBRICS_GRADING_PROMPT_VARIABLES,
};

export const DEFAULT_K = 3;

export const DEFAULT_GRADING_MODEL_CONFIG = [
  {
    id: 'endpoint_LiteLLMStrategy_1',
    url: 'https://litellm-proxy.ml-serving-internal.scale.com/v1/chat/completions',
    params: {
      model: 'o3-mini',
      reasoning_effort: 'high',
      provider: 'openai',
    },
  },
];

// Estimates from https://app.datadoghq.com/dashboard/qtp-rrr-tmp/self-serve-passk-dashboard
export const MODEL_NAME_TO_COST_PER_REQUEST_ESTIMATE = {
  'sonar-deep-research': 1.05,
  'gemini-2.5-pro-preview-06-05': 0.4,
  'claude-sonnet-4-20250514': 0.4,
  o3: 0.35,
  'o4-mini': 0.15,
};

export const getEstimatedCostPerTask = (modelName: string, k: number): number | null => {
  const costPerRequest =
    MODEL_NAME_TO_COST_PER_REQUEST_ESTIMATE[
      modelName as keyof typeof MODEL_NAME_TO_COST_PER_REQUEST_ESTIMATE
    ];
  return costPerRequest !== undefined ? _.round(costPerRequest * k, 2) : null;
};

export const getPresetModelOptions = (
  overrideApiKey: string,
): { config: ModelConfig; k: number }[] => [
  {
    config: {
      id: 'endpoint_LiteLLMStrategy_gemini_2_5_06_05',
      url: 'https://litellm-proxy.ml-serving-internal.scale.com/v1/chat/completions',
      params: {
        model: 'gemini-2.5-pro-preview-06-05',
        provider: 'gemini',
        overrideApiKey,
        timeout: 30 * 60 * 1000, // 30 minutes
      },
    },
    k: 3,
  },
  {
    config: {
      id: 'endpoint_LiteLLMStrategy_o3',
      url: 'https://litellm-proxy.ml-serving-internal.scale.com/v1/chat/completions',
      params: {
        model: 'o3',
        reasoning_effort: 'high',
        provider: 'openai',
        overrideApiKey,
        timeout: 30 * 60 * 1000, // 30 minutes
      },
    },
    k: 3,
  },
  {
    config: {
      id: 'endpoint_LiteLLMStrategy_o4_mini',
      url: 'https://litellm-proxy.ml-serving-internal.scale.com/v1/chat/completions',
      params: {
        model: 'o4-mini',
        reasoning_effort: 'high',
        provider: 'openai',
        overrideApiKey,
        timeout: 30 * 60 * 1000, // 30 minutes
      },
    },
    k: 3,
  },
  {
    config: {
      id: 'endpoint_LiteLLMStrategy_claude_4',
      url: 'https://litellm-proxy.ml-serving-internal.scale.com/v1/chat/completions',
      params: {
        model: 'claude-sonnet-4-20250514',
        provider: 'anthropic',
        max_tokens: 64000,
        extra_body: {
          thinking: {
            type: 'enabled',
            budget_tokens: 63999,
          },
        },
        overrideApiKey,
        timeout: 20 * 60 * 1000, // 20 minutes
      },
    },
    k: 3,
  },
  {
    config: {
      id: 'endpoint_LiteLLMStrategy_perplexity',
      url: 'https://litellm-proxy.ml-serving-internal.scale.com/v1/chat/completions',
      params: {
        model: 'sonar-deep-research',
        provider: 'perplexity',
        overrideApiKey,
        timeout: 20 * 60 * 1000, // 20 minutes
      },
    },
    k: 3,
  },
  {
    config: {
      id: 'endpoint_GoldfishStrategyV2_grok_research_0510',
      url: 'https://research-models.api.x.ai/',
      params: {
        model: 'research-g3r-0510',
        deferred: true,
        temperature: 1,
        endpoint_url: 'https://research-models.api.x.ai/v1/chat/completions',
        timeout: 20 * 60 * 1000, // 20 minutes
        max_tokens: 128000,
      },
    },
    k: 3,
  },
  {
    config: {
      id: 'endpoint_GoldfishStrategyV2_grok_3_mini_beta',
      url: 'https://research-models.api.x.ai/',
      params: {
        model: 'grok-3-mini-beta',
        deferred: true,
        temperature: 1,
        endpoint_url: 'https://research-models.api.x.ai/v1/chat/completions',
        timeout: 20 * 60 * 1000, // 20 minutes
        max_tokens: 128000,
      },
    },
    k: 3,
  },
  {
    config: {
      id: 'endpoint_FlamingoGraphStrategy_lazarus_math',
      url: 'https://graph-genai.facebook.com/v18.0',
      params: {
        model: 'cappuccino',
        temperature: 1,
        maxMessages: 1,
        sampleModelParams: { workstreamName: 'reasoning_r8_math_feedback' },
        dialogId: '60da327c-397f-4ae6-bcb0-f935b1de793d',
        workstream: 'reasoning_r8_math_feedback',
        useExtendedTimeout: true,
        enableFeedback: true,
        use_dialog_types: true,
        enableDialog: true,
      },
    },
    k: 3,
  },
  {
    config: {
      id: 'endpoint_FlamingoGraphStrategy_lazarus_stem',
      url: 'https://graph-genai.facebook.com/v18.0',
      params: {
        model: 'cortado',
        temperature: 1,
        maxMessages: 1,
        sampleModelParams: { workstreamName: 'reasoning_r8_stem_feedback' },
        dialogId: '60da327c-397f-4ae6-bcb0-f935b1de793d',
        workstream: 'reasoning_r8_stem_feedback',
        useExtendedTimeout: true,
        enableFeedback: true,
        use_dialog_types: true,
        enableDialog: true,
      },
    },
    k: 3,
  },
  {
    config: {
      id: 'endpoint_FlamingoGraphStrategy_lazarus_non_stem',
      url: 'https://graph-genai.facebook.com/v18.0',
      params: {
        model: 'cafe_au_lait',
        temperature: 1,
        maxMessages: 1,
        sampleModelParams: { workstreamName: 'reasoning_r1_non_stem_feedback' },
        dialogId: '8ef66ba9-2aeb-4bb7-a037-b36fe9b0b8c5',
        workstream: 'reasoning_r1_non_stem_feedback',
        useExtendedTimeout: true,
        enableFeedback: true,
        use_dialog_types: true,
        enableDialog: true,
      },
    },
    k: 3,
  },
  {
    config: {
      id: 'endpoint_GoldfishStrategyV2_grok_4',
      url: 'https://research-models.api.x.ai/',
      params: {
        model: 'grok-4-0709',
        deferred: true,
        temperature: 0.7,
        endpoint_url: 'https://api.x.ai/v1/chat/completions',
        timeout: 20 * 60 * 1000, // 20 minutes
        max_tokens: 128000,
      },
    },
    k: 3,
  },
];

export const DEFAULT_SNOWFLAKE_SQL_CARD: SnowflakeSqlContext = {
  cardType: CardType.SNOWFLAKE_SQL,
  code: '',
};

export const DEFAULT_HLE_SNOWFLAKE_SQL_CARD: SnowflakeSqlContext = {
  cardType: CardType.SNOWFLAKE_SQL,
  code: `select
    pto.task as task_id,
    pto.prompt as prompt,
    pto.topic as topic,
    pto.gtfa as gtfa
from VIEW.PCP_MAPLEV3_TASK_OUTCOMES pto
where prompt is not null and topic is not null and gtfa is not null
limit 1`,
};

type BadgeProps = React.ComponentProps<typeof Badge>;
export const RUN_TYPE_TO_COLOR: Record<RunType, BadgeProps['color']> = {
  [RunType.Adhoc]: 'neutral',
  [RunType.Continuous]: 'info',
};
export const RUN_STATUS_TO_COLOR: Record<PassAtKRunStatus, BadgeProps['color']> = {
  [PassAtKRunStatus.Pending]: 'neutral',
  [PassAtKRunStatus.Processing]: 'warning',
  [PassAtKRunStatus.Completed]: 'success',
  [PassAtKRunStatus.Error]: 'error',
  [PassAtKRunStatus.Cancelled]: 'neutral',
};
